import { RootState } from '../../store.model';
import {
  CommanderControlUnitChannelFilterLogicalObject,
  CommanderMonitoringFilterLogicalObject,
} from '../api/util/filter/filter.model';
import MonitoringAPI from '../api/MonitoringAPI';
import { createSelector } from '@reduxjs/toolkit';
import { UUID } from '../api/monitoring.model';
import { getCUsTranslationsSelector } from '../../CUTranslation/selectors';
import { mapSensordataToSTChartLine } from '../api/util/mapSensordataToSTChartLine';
import { ShowLevelOption } from '../../groups/selectors/getLevelOfView';
import useLabStateFilters from '../../../components/DeviceManager/Device/Monitoring/components/Lab/useLabStateFilters';
import { ZipDeviceControlUnit } from '../../controlUnit/controlUnit.model';
import ControlUnitAPI from '../../controlUnit/api/ControlUnitAPI';
import { mapEventsDigitalToSTCharTimeLine } from '../api/util/mapEventsDigitalToSTCharTimeLine';
import moment from 'moment';
import { STTimeLineChartData } from '../api/monitoring-ui.model';
import _, { merge } from 'lodash';

interface SelectorParams {
  devicesControlUnits: ZipDeviceControlUnit[];
  start: number;
  end: number;
  showLevelOption: ShowLevelOption;
}

interface AnalogSelectorParams extends SelectorParams {
  preferredTemperatureUnit: string;
  analogFilterObjectEvents: CommanderMonitoringFilterLogicalObject;
}

interface EventsSelectorParams extends SelectorParams {
  digitalFilterObjectEvents: CommanderMonitoringFilterLogicalObject;
  connectionDigitalFilterObject?: CommanderMonitoringFilterLogicalObject;
}

interface LabStateSelectorParams extends SelectorParams {
  labStateFilters: ReturnType<typeof useLabStateFilters>;
}

type SensorInformationParams = EventsSelectorParams &
  LabStateSelectorParams &
  AnalogSelectorParams;

interface MonitoringParamsSelector {
  deviceId: string;
  start: number;
  end: number;
  commanderFilterObject: CommanderControlUnitChannelFilterLogicalObject;
  includeCloudGenerated?: boolean;
  deviceName: string;
  showLevelOption: ShowLevelOption;
  preferredTemperatureUnit?: string;
  /** Connection channels used to read the broker connection status from cache */
  connectionChannels?: string[];
}

const BROKER_CONNECTION_EVENT_ID = 'brokerConnection';

/**
 * Flatten the enabled connection channels for a device, matching the args used
 * to fetch the connection info so the cached broker status can be read back.
 */
function getConnectionChannelsForDevice(
  connectionDigitalFilterObject:
    | CommanderMonitoringFilterLogicalObject
    | undefined,
  deviceId: string
): string[] {
  return Object.values(connectionDigitalFilterObject?.[deviceId] || {}).flatMap(
    (controlUnitObject = {}) =>
      Object.entries(controlUnitObject)
        .filter(([_key, value]) => !!value)
        .map(([key]) => key)
  );
}
const selectorSensorData = createSelector(
  [
    (state: RootState) => state,
    getCUsTranslationsSelector,
    (_: RootState, params: MonitoringParamsSelector) => params.deviceId,
    (_: RootState, params: MonitoringParamsSelector) => params.start,
    (_: RootState, params: MonitoringParamsSelector) => params.end,
    (_: RootState, params: MonitoringParamsSelector) =>
      params.commanderFilterObject,

    (_: RootState, params: MonitoringParamsSelector) => params.deviceName,
    (_: RootState, params: MonitoringParamsSelector) => params.showLevelOption,
    (_: RootState, params: MonitoringParamsSelector) =>
      params.preferredTemperatureUnit,
    (state: RootState, params: MonitoringParamsSelector) =>
      ControlUnitAPI.endpoints.getControlUnitById.select(params.deviceId),
    (state: RootState, params: MonitoringParamsSelector) =>
      MonitoringAPI.endpoints.getSensordataByDeviceID.select({
        deviceId: params.deviceId,
        start: params.start,
        end: params.end,
        commanderFilterObject: params.commanderFilterObject,
        includeCloudGenerated: false,
      }),
  ],
  (
    state,
    CUsTranslate,
    deviceId,
    _start,
    _end,
    _commanderFilterObject,
    deviceName,
    showLevelOption,
    preferredTemperatureUnit = '',
    controlUnitState,
    sensorData
  ) => {
    const sensorDataValues = sensorData(state).data || [];
    const controlUnitValues = controlUnitState(state).data?.entities ?? {};

    return sensorDataValues.map((sensorData) =>
      mapSensordataToSTChartLine(
        sensorData,
        deviceId,
        deviceName || '',
        CUsTranslate,
        showLevelOption,
        controlUnitValues,
        preferredTemperatureUnit
      )
    );
  }
);
const selectorEVentsData = createSelector(
  [
    (state: RootState) => state,
    getCUsTranslationsSelector,
    (_: RootState, params: MonitoringParamsSelector) => params.deviceId,
    (_: RootState, params: MonitoringParamsSelector) => params.start,
    (_: RootState, params: MonitoringParamsSelector) => params.end,
    (_: RootState, params: MonitoringParamsSelector) =>
      params.commanderFilterObject,

    (_: RootState, params: MonitoringParamsSelector) => params.deviceName,
    (_: RootState, params: MonitoringParamsSelector) => params.showLevelOption,
    (state: RootState, params: MonitoringParamsSelector) =>
      MonitoringAPI.endpoints.getEventsByID.select({
        deviceId: params.deviceId,
        start: moment(params.start).add(-1, 'day').valueOf(),
        end: moment(params.end).add(1, 'day').valueOf(),
        commanderFilterObject: params.commanderFilterObject,
      })(state).data || [],
    (state: RootState, params: MonitoringParamsSelector) =>
      ControlUnitAPI.endpoints.getControlUnitById.select(params.deviceId)(state)
        .data?.entities ?? {},
    (state: RootState, params: MonitoringParamsSelector) =>
      MonitoringAPI.endpoints.getConnectionInfoByID.select({
        deviceId: params.deviceId,
        start: moment(params.start).add(-1, 'day').valueOf(),
        end: moment(params.end).add(1, 'day').valueOf(),
        channels: params.connectionChannels ?? [],
      })(state).data || [],
  ],
  (
    _state,
    CUsTranslate,
    deviceId,
    start,
    end,
    _commanderFilterObject,
    deviceName,
    showLevelOption,
    eventDataInfo,
    controlUnitState,
    connectionInfo
  ) => {
    const brokerConnectionEvents = connectionInfo
      .flatMap((controlUnit) => controlUnit.events)
      .filter((event) => event.eventID === BROKER_CONNECTION_EVENT_ID)
      .map((event) => ({
        ...event,
        timestamp: Math.floor(event.timestamp / 1000),
      }));
    return {
      [deviceId]: mapEventsDigitalToSTCharTimeLine(
        eventDataInfo.map((item) => ({
          ...item,
          deviceId: deviceId,
          deviceName: deviceName || '',
          events: item.events.map((event) => ({
            ...event,
            timestamp: Math.floor(event.timestamp / 1000),
          })),
        })),
        Math.floor(start / 1000),
        Math.floor(end / 1000),
        CUsTranslate,
        showLevelOption,
        controlUnitState,
        brokerConnectionEvents
      ),
    };
  }
);
const selectorLabData = createSelector(
  [
    (state: RootState) => state,
    getCUsTranslationsSelector,
    (_: RootState, params: MonitoringParamsSelector) => params.deviceId,
    (_: RootState, params: MonitoringParamsSelector) => params.start,
    (_: RootState, params: MonitoringParamsSelector) => params.end,
    (_: RootState, params: MonitoringParamsSelector) =>
      params.commanderFilterObject,
    (_: RootState, params: MonitoringParamsSelector) => params.deviceName,
    (_: RootState, params: MonitoringParamsSelector) => params.showLevelOption,
  ],
  (
    state,
    CUsTranslate,
    deviceId,
    start,
    end,
    commanderFilterObject,
    deviceName,
    showLevelOption
  ) => {
    const connectionData = MonitoringAPI.endpoints.getConnectionInfoByID;

    const channels = Object.values(commanderFilterObject).flatMap(
      (controlUnitObject = {}) =>
        Object.entries(controlUnitObject)
          .filter(([_key, value]) => !!value)
          .map(([key, _value]) => key)
    );
    const connectionInfo =
      connectionData.select({
        deviceId: deviceId,
        start: moment(start).add(-1, 'day').valueOf(),
        end: moment(end).add(1, 'day').valueOf(),
        channels,
      })(state).data || [];

    const controlUnitState =
      ControlUnitAPI.endpoints.getControlUnitById.select(deviceId)(state).data
        ?.entities ?? {};

    return {
      [deviceId]: mapEventsDigitalToSTCharTimeLine(
        connectionInfo.map((item) => ({
          ...item,
          deviceId: deviceId,
          deviceName: deviceName || '',
          events: item.events.map((event) => ({
            ...event,
            timestamp: Math.floor(event.timestamp / 1000),
          })),
        })),
        Math.floor(start / 1000),
        Math.floor(end / 1000),
        CUsTranslate,
        showLevelOption,
        controlUnitState
      ),
    };
  }
);

const selectorBandwidthData = createSelector(
  [
    (state: RootState) => state,
    getCUsTranslationsSelector,
    (_: RootState, props: MonitoringParamsSelector) =>
      MonitoringAPI.endpoints.getSensordataByDeviceID.select({
        deviceId: props.deviceId,
        start: props.start,
        end: props.end,
        commanderFilterObject: props.commanderFilterObject,
        includeCloudGenerated: true,
      }),
    (_: RootState, props: MonitoringParamsSelector) =>
      ControlUnitAPI.endpoints.getControlUnitById.select(props.deviceId),
    (_: RootState, props: MonitoringParamsSelector) => props.deviceId,
    (_: RootState, props: MonitoringParamsSelector) => props.deviceName,
    (_: RootState, props: MonitoringParamsSelector) => props.showLevelOption,
    (_: RootState, props: MonitoringParamsSelector) =>
      props.preferredTemperatureUnit,
  ],
  (
    state,
    CUsTranslate,
    sensorData,
    controlUnitState,
    deviceId,
    deviceName,
    showLevelOption,
    preferredTemperatureUnit = ''
  ) => {
    const sensorDataValues = sensorData(state).data || [];
    const controlUnitValues = controlUnitState(state).data?.entities ?? {};
    return sensorDataValues.map((sensorData) =>
      mapSensordataToSTChartLine(
        sensorData,
        deviceId,
        deviceName || '',
        CUsTranslate,
        showLevelOption,
        controlUnitValues,
        preferredTemperatureUnit
      )
    );
  }
);

export const getSensorInformation = createSelector(
  [
    (state: RootState) => state,
    (_: RootState, params: SensorInformationParams) =>
      params.devicesControlUnits,
    (_: RootState, params: SensorInformationParams) => params.start,
    (_: RootState, params: SensorInformationParams) => params.end,
    (_: RootState, params: SensorInformationParams) =>
      params.analogFilterObjectEvents,
    (_: RootState, params: SensorInformationParams) =>
      params.digitalFilterObjectEvents,
    (_: RootState, params: SensorInformationParams) => params.showLevelOption,
    (_: RootState, params: SensorInformationParams) =>
      params.preferredTemperatureUnit,
    (_: RootState, params: SensorInformationParams) => params.labStateFilters,
  ],
  (
    state,
    devicesControlUnits,
    start,
    end,
    analogFilterObjectEvents,
    digitalFilterObjectEvents,
    showLevelOption,
    preferredTemperatureUnit,
    labStateFilters
  ) => {
    return getSensorDataWithChartFormat({
      devicesControlUnits,
      digitalFilterObjectEvents,
      analogFilterObjectEvents,
      start,
      end,
      showLevelOption,
      preferredTemperatureUnit,
      labStateFilters,
      state,
    });
  }
);

const devicesSensorData = createSelector(
  (state: RootState) => state,
  (_: RootState, props: AnalogSelectorParams) => props.devicesControlUnits,
  (_: RootState, props: AnalogSelectorParams) => props.start,
  (_: RootState, props: AnalogSelectorParams) => props.end,
  (_: RootState, props: AnalogSelectorParams) => props.analogFilterObjectEvents,
  (_: RootState, props: AnalogSelectorParams) => props.showLevelOption,
  (_: RootState, props: AnalogSelectorParams) => props.preferredTemperatureUnit,
  (
    state,
    devicesControlUnits,
    start,
    end,
    analogFilterObjectEvents,
    showLevelOption,
    preferredTemperatureUnit
  ) => {
    return devicesControlUnits
      .map((deviceControlUnit: ZipDeviceControlUnit) =>
        selectorSensorData(state, {
          deviceId: deviceControlUnit.deviceId,
          start,
          end,
          commanderFilterObject:
            analogFilterObjectEvents[deviceControlUnit.deviceId] || {},
          includeCloudGenerated: false,
          deviceName: deviceControlUnit.deviceName || '',
          showLevelOption,
          preferredTemperatureUnit: preferredTemperatureUnit ?? '',
        })
      )
      .flat();
  }
);
const devicesEventsData = createSelector(
  (state: RootState) => state,
  (_: RootState, props: EventsSelectorParams) => props.devicesControlUnits,
  (_: RootState, props: EventsSelectorParams) => props.start,
  (_: RootState, props: EventsSelectorParams) => props.end,
  (_: RootState, props: EventsSelectorParams) =>
    props.digitalFilterObjectEvents,
  (_: RootState, props: EventsSelectorParams) => props.showLevelOption,
  (_: RootState, props: EventsSelectorParams) =>
    props.connectionDigitalFilterObject,
  (
    state,
    devicesControlUnits,
    start,
    end,
    digitalFilterObjectEvents,
    showLevelOption,
    connectionDigitalFilterObject
  ) => {
    const digitalDataChart: Record<UUID, STTimeLineChartData>[] =
      devicesControlUnits.map((deviceControlUnit: ZipDeviceControlUnit) => {
        return selectorEVentsData(state, {
          deviceId: deviceControlUnit.deviceId,
          start,
          end,
          commanderFilterObject:
            digitalFilterObjectEvents[deviceControlUnit.deviceId] || {},
          includeCloudGenerated: false,
          deviceName: deviceControlUnit.deviceName || '',
          showLevelOption,
          connectionChannels: getConnectionChannelsForDevice(
            connectionDigitalFilterObject,
            deviceControlUnit.deviceId
          ),
        });
      });

    return digitalDataChart.reduce((acc, curr) => {
      return { ...acc, ...curr };
    }, {});
  }
);

const devicesLabData = createSelector(
  (state: RootState) => state,
  (_: RootState, props: EventsSelectorParams) => props.devicesControlUnits,
  (_: RootState, props: EventsSelectorParams) => props.start,
  (_: RootState, props: EventsSelectorParams) => props.end,
  (_: RootState, props: EventsSelectorParams) =>
    props.digitalFilterObjectEvents,
  (_: RootState, props: EventsSelectorParams) => props.showLevelOption,
  (
    state,
    devicesControlUnits,
    start,
    end,
    cloudGeneratedFilterLogicalObject = {},
    showLevelOption
  ) => {
    const data = devicesControlUnits.map(
      (deviceControlUnit: ZipDeviceControlUnit) =>
        selectorLabData(state, {
          deviceId: deviceControlUnit.deviceId,
          start,
          end,
          commanderFilterObject:
            cloudGeneratedFilterLogicalObject[deviceControlUnit.deviceId] || {},
          includeCloudGenerated: false,
          deviceName: deviceControlUnit.deviceName || '',
          showLevelOption,
        })
    );

    return data.reduce((acc, curr) => {
      return { ...acc, ...curr };
    }, {});
  }
);

const devicesBandwidthData = createSelector(
  (state: RootState) => state,
  (_: RootState, props: AnalogSelectorParams) => props.devicesControlUnits,
  (_: RootState, props: AnalogSelectorParams) => props.start,
  (_: RootState, props: AnalogSelectorParams) => props.end,
  (_: RootState, props: AnalogSelectorParams) => props.analogFilterObjectEvents,
  (_: RootState, props: AnalogSelectorParams) => props.showLevelOption,
  (_: RootState, props: AnalogSelectorParams) => props.preferredTemperatureUnit,
  (
    state,
    devicesControlUnits,
    start,
    end,
    connectionDigitalFilterObject = {},
    showLevelOption,
    preferredTemperatureUnit
  ) => {
    return devicesControlUnits
      .map((deviceControlUnit: ZipDeviceControlUnit) => {
        return selectorBandwidthData(state, {
          deviceId: deviceControlUnit.deviceId,
          start,
          end,
          commanderFilterObject:
            connectionDigitalFilterObject[deviceControlUnit.deviceId] ?? {},
          includeCloudGenerated: true,
          deviceName: deviceControlUnit.deviceName || '',
          showLevelOption,
          preferredTemperatureUnit: preferredTemperatureUnit ?? '',
        });
      })
      .flat();
  }
);

export const getSensorDataWithChartFormat = (params: {
  devicesControlUnits: ZipDeviceControlUnit[];
  digitalFilterObjectEvents: CommanderMonitoringFilterLogicalObject;
  analogFilterObjectEvents: CommanderMonitoringFilterLogicalObject;
  start: number;
  end: number;
  showLevelOption: ShowLevelOption;
  preferredTemperatureUnit: string;
  labStateFilters: ReturnType<typeof useLabStateFilters>;
  state: RootState;
}) => {
  const {
    devicesControlUnits,
    digitalFilterObjectEvents,
    analogFilterObjectEvents,
    start,
    end,
    showLevelOption,
    preferredTemperatureUnit,
    state,
  } = params;

  const {
    connectionDigitalFilterControls: [connectionDigitalFilterObject],
    cloudGeneratedFilterControls: [cloudGeneratedFilterLogicalObject],
    labFiltersFeatures: { labBandwidthFeature, labConnectionFeature },
  } = params.labStateFilters;

  const sensorDataChart = devicesSensorData(state, {
    devicesControlUnits,
    start,
    end,
    analogFilterObjectEvents,
    showLevelOption,
    preferredTemperatureUnit,
  });

  const digitalDataChart = devicesEventsData(state, {
    devicesControlUnits,
    start,
    end,
    digitalFilterObjectEvents,
    showLevelOption,
    connectionDigitalFilterObject,
  });

  const dicConnectionInfo = devicesLabData(state, {
    devicesControlUnits,
    start,
    end,
    digitalFilterObjectEvents: connectionDigitalFilterObject,
    showLevelOption,
  });

  const cloudGenerated = devicesBandwidthData(state, {
    devicesControlUnits,
    start,
    end,
    analogFilterObjectEvents: cloudGeneratedFilterLogicalObject,
    showLevelOption,
    preferredTemperatureUnit,
  });

  return {
    sensorData: Array.prototype.concat(
      sensorDataChart,
      labBandwidthFeature ? cloudGenerated : []
    ),
    digitalData: merge(
      {},
      digitalDataChart,
      labConnectionFeature ? dicConnectionInfo : {}
    ),
  };
};
